/* eslint-disable @typescript-eslint/naming-convention */
import xor from 'lodash/xor';
import without from 'lodash/without';
import { takeLatest, put, call, select, all } from 'redux-saga/effects';
import { initialize } from 'redux-form';

import {
  SETTING_INVENTORY_PAGE_REQUEST,
  SETTING_INVENTORY_UPDATE_SETTING,
} from '../../actions/setting/inventory/constant';
import { showPageLoadingSpinner } from '../../actions/loadingSpinner';
import * as actNotification from '../../actions/notifications';
import {
  settingInventorySetRoles,
  settingInventorySetPermissions,
} from '../../actions/setting/inventory';
import { InventorySettingSaveAction } from '../../actions/setting/inventory/types';

import { CONFIG_SELECTIONS } from '../../constants/accountSetting/inventory';
import * as apiInventorySettings from '../../api/inventorySettings';
import * as apiRoles from '../../api/roles/roles';
import * as apiEmployees from '../../api/employees/employees';
import * as apiRole from '../../api/employees/role/role';
import { GetInventorySettingsResponse } from '../../api/settings/types';
import { assertErrorHasMessage } from '../../utils/assert';
import * as clevertapInventorySettings from '../../clevertap/settings/inventory';

import type {
  InventorySettings,
  InventorySettingsForm,
  InventorySettingsPermission,
  RoleChanged,
} from '../../types/settings/InventorySetting';
import type { Permission, Role } from '../../types/roles/Role';

const getUser = (state) => state.moka.user;

function getRelatedRoles(roles: Role[], permissionId: number) {
  return roles.filter((role) => {
    const roleIndex = role.permissions.findIndex(
      (rolePermission) => rolePermission.id === permissionId
    );
    return roleIndex !== -1;
  });
}

function* generatePermissions(name: string) {
  const roles: Role[] = yield select(
    (state: any) => state.inventorySettings.roles
  );
  const permissions: Permission[] = yield select(
    (state: any) => state.inventorySettings.permissions
  );
  const selectedConfig = CONFIG_SELECTIONS.find(
    (configSelection) => configSelection.name === name
  );

  const matchedPermissions: InventorySettingsPermission[] =
    selectedConfig!.options.advanced.permissions.reduce((arr, permission) => {
      const selectedPermission = permissions.find(
        ({ sub_menu_name }) => sub_menu_name === permission.label
      );

      if (selectedPermission) {
        const newArr = [...arr];
        newArr.push({
          ...selectedPermission,
          labelUi: permission.labelUi,
          testId: permission.testId,
          roles: getRelatedRoles(roles, selectedPermission.id),
        });
        return newArr;
      }

      return arr;
    }, [] as InventorySettingsPermission[]);

  return matchedPermissions;
}

function* initInventorySettingsForm(inventorySettings: InventorySettings) {
  const {
    advanced_transfer: isAdvancedTransfer,
    advanced_purchase_order: isAdvancedPurchaseOrder,
    advanced_adjustment: isAdvancedAdjustment,
  } = inventorySettings;

  const form: InventorySettingsForm = {
    purchase_order_config: {
      config_type: isAdvancedPurchaseOrder ? 'advanced' : 'simple',
      permissions: yield call(generatePermissions, 'purchase_order_config'),
    },
    transfer_config: {
      config_type: isAdvancedTransfer ? 'advanced' : 'simple',
      permissions: yield call(generatePermissions, 'transfer_config'),
    },
    adjustment_config: {
      config_type: isAdvancedAdjustment ? 'advanced' : 'simple',
      permissions: yield call(generatePermissions, 'adjustment_config'),
    },
  };

  yield put(initialize('inventory', form));
}

function* saveInventoryConfigType(inventorySettingForm: InventorySettingsForm) {
  const {
    purchase_order_config: purchaseOrderConfig,
    transfer_config: transferConfig,
    adjustment_config: adjustmentConfig,
  } = inventorySettingForm;

  const updatedInventorySettings: GetInventorySettingsResponse = yield call(
    apiInventorySettings.updateInventorySettings,
    {
      advanced_adjustment: adjustmentConfig.config_type === 'advanced',
      advanced_purchase_order: purchaseOrderConfig.config_type === 'advanced',
      advanced_transfer: transferConfig.config_type === 'advanced',
    }
  );

  yield call(initInventorySettingsForm, updatedInventorySettings);
}

function* saveAllRoles(inventorySettingForm: InventorySettingsForm) {
  const {
    purchase_order_config: purchaseOrderConfig,
    transfer_config: transferConfig,
    adjustment_config: adjustmentConfig,
  } = inventorySettingForm;

  const roles: Role[] = yield select(
    (state: any) => state.inventorySettings.roles
  );
  const rolesChanged: RoleChanged[] = [];

  let formPermissions: any[] = [];
  if (purchaseOrderConfig.config_type === 'advanced') {
    formPermissions = [...formPermissions, ...purchaseOrderConfig.permissions];
  }
  if (adjustmentConfig.config_type === 'advanced') {
    formPermissions = [...formPermissions, ...adjustmentConfig.permissions];
  }
  if (transferConfig.config_type === 'advanced') {
    formPermissions = [...formPermissions, ...transferConfig.permissions];
  }

  const formPermissionIds = formPermissions.map((permission) => permission.id);

  roles.forEach((role) => {
    const userPermissionIds = role.permissions.map(
      (permission) => permission.id
    );
    const cleanUserPermissionIds = without(
      userPermissionIds,
      ...formPermissionIds
    );
    const userFormPermissionIds = formPermissions
      .filter((permission) => {
        const permissionIndex = permission.roles.findIndex(
          (permissionRole) => permissionRole.id === role.id
        );
        return permissionIndex !== -1;
      })
      .map((permission) => permission.id);
    const newUserPermissionIds = [
      ...cleanUserPermissionIds,
      ...userFormPermissionIds,
    ];
    const isChanged = xor(newUserPermissionIds, userPermissionIds).length > 0;

    if (isChanged) {
      rolesChanged.push({
        id: role.id,
        name: role.name,
        permissions: newUserPermissionIds,
      });
    }
  });

  yield all(
    rolesChanged.map((roleChanged) =>
      call(apiRole.updateRole, roleChanged.id, {
        name: roleChanged.name,
        permissions: roleChanged.permissions,
      })
    )
  );
}

export function* fetchRoles() {
  try {
    const { data } = yield call(apiRoles.getRolesV1, {
      params: { deep: true },
    });

    yield put(settingInventorySetRoles(data.roles));
  } catch (e) {
    assertErrorHasMessage(e);
    yield put(actNotification.error(e.message));
  }
}

export function* fetchPermissions() {
  try {
    const userId = yield select((state: any) => state.moka.user.id);
    const { data } = yield call(apiEmployees.getPermission, {
      userId,
      params: { usable: true },
    });

    yield put(settingInventorySetPermissions(data.permissions));
  } catch (e) {
    assertErrorHasMessage(e);
    yield put(actNotification.error(e.message));
  }
}

export function* fetchConfig() {
  try {
    const inventorySettings: GetInventorySettingsResponse = yield call(
      apiInventorySettings.getInventorySettings
    );

    yield call(initInventorySettingsForm, inventorySettings);
  } catch (e) {
    assertErrorHasMessage(e);
    yield put(actNotification.error(e.message));
    yield call(initInventorySettingsForm, {
      advanced_adjustment: false,
      advanced_purchase_order: false,
      advanced_transfer: false,
    });
  }
}

export function* pageRequest() {
  yield put(showPageLoadingSpinner(true));

  try {
    yield call(fetchRoles);
    yield call(fetchPermissions);
    yield call(fetchConfig);
  } finally {
    yield put(showPageLoadingSpinner(false));
  }
}

export function* updateSettings(request: InventorySettingSaveAction) {
  const { inventorySettingForm, additional } = request.payload!;

  yield put(showPageLoadingSpinner(true));
  try {
    yield call(saveAllRoles, inventorySettingForm);
    yield call(saveInventoryConfigType, inventorySettingForm);
    yield put(
      actNotification.success('Inventory Settings applied successfully.')
    );
    yield call(pageRequest);

    const user = yield select(getUser);
    if (additional.advancedTransferEnabled) {
      clevertapInventorySettings.advancedTransferEnabled(user.email);
    }
    if (additional.advancedPOEnabled) {
      clevertapInventorySettings.advancedPOEnabled(user.email);
    }
  } catch (e) {
    assertErrorHasMessage(e);
    yield put(actNotification.error(e.message));
  } finally {
    yield put(showPageLoadingSpinner(false));
  }
}

export default function* inventorySettingSagas() {
  yield takeLatest(SETTING_INVENTORY_PAGE_REQUEST, pageRequest);
  yield takeLatest(SETTING_INVENTORY_UPDATE_SETTING, updateSettings);
}
